/**
 * useTaskPhaseReport
 *
 * When a task that maps to a phase transition is completed, automatically
 * runs cascadePhaseTimingUpdate with today's date — the same behaviour as
 * manually submitting a report pin with today's date.
 */

import { useCallback } from 'react'
import { useUpdateCropPhase } from '~/api/supabase-mutations'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCropsContextDispatch, cropEdited } from '~/common/context/crops/crops-context'
import { cascadePhaseTimingUpdate, mergePhaseUpdates } from '~/crop/crop-schedule/utils/cascade-phase-timing'
import { TASK_PHASE_TRANSITION_MAP } from '~/crop/crop-schedule/utils/task-phase-config'

export const useTaskPhaseReport = () => {
  const { updatePhase } = useUpdateCropPhase()
  const cropsDispatch = useCropsContextDispatch()

  /**
   * Run the phase cascade for a completed task.
   * No-ops silently when the task has no linked phase or no active schedule.
   *
   * @param {Object} task - Task object (must have task_code, crop_id)
   */
  const reportPhaseTransitionOnComplete = useCallback(async (task) => {
    const phaseType = TASK_PHASE_TRANSITION_MAP[task.task_code]
    if (!phaseType || !task.crop_id) return

    try {
      // Get the active schedule for this crop
      const { data: schedule } = await supabaseGetter.cropSchedules.getActiveByCrop(task.crop_id)
      if (!schedule) return

      // Get phases for the schedule (already sorted by step asc from getter)
      const { data: rawPhases } = await supabaseGetter.cropPhases.getBySchedule(schedule.id)
      if (!rawPhases?.length) return

      const sorted = [...rawPhases].sort((a, b) => a.step - b.step)

      // Find the phase index that this task transitions INTO
      const targetIdx = sorted.findIndex(p => p.phase_type_code === phaseType)
      if (targetIdx === -1) return

      // Build the minimal phase format expected by cascadePhaseTimingUpdate
      const phasesForCascade = sorted.map(p => ({
        phaseId: p.id,
        est_started_at: p.est_started_at,
        est_duration: p.est_duration,
      }))

      const boundaryType = targetIdx === 0 ? 'start' : targetIdx - 1
      const boundaryDate = new Date().toISOString()

      const rawUpdates = cascadePhaseTimingUpdate(phasesForCascade, boundaryType, boundaryDate)
      const updates = mergePhaseUpdates(rawUpdates)
      if (!updates.length) return

      for (const { phaseId, ...fields } of updates) {
        if (!phaseId) continue
        await updatePhase(phaseId, fields)
      }

      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropPhases.invalidate()
      cropsDispatch(cropEdited({ cropId: task.crop_id }))
    } catch (err) {
      console.error('Phase report on task complete failed:', err)
    }
  }, [updatePhase, cropsDispatch])

  return { reportPhaseTransitionOnComplete }
}
